import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';
import {
  ClockIcon,
  PlayIcon,
  StopIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  PlusIcon,
  UserIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowPathIcon,
  EyeIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon as ClockOutlineIcon,
  MapIcon,
  UserCircleIcon,
  BriefcaseIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';

// Import RTK Query hooks
import {
  useGetAttendanceQuery,
  useCreateAttendanceMutation,
  useUpdateAttendanceMutation,
  useDeleteAttendanceMutation,
  useClockInMutation,
  useClockOutMutation,
  useGetMyAttendanceQuery,
} from '../../services/attendanceApi';
import { useGetEmployeesQuery } from '../../services/employeeApi';
import { useGetMyCompanyQuery } from '../../services/companyApi';
import { useGetBranchesQuery } from '../../services/branchApi';
import { useGetShiftsQuery } from '../../services/shiftApi';
import { useGetDepartmentsQuery } from '../../services/departmentApi';
import { useGetDesignationsQuery } from '../../services/designationApi';

// Import Redux actions
import {
  setAttendance,
  setFilters as setAttendanceFilters,
} from '../../store/slices/attendanceSlice';

// Import AttendanceCamera component
import AttendanceCamera from '../../components/AttendanceCamera';

// Import RBAC utilities
import { hasPermission, USER_ROLES, isAdmin } from '../../utils/rbac';

const Attendance = () => {
  const { t } = useTranslation(['common', 'attendance']);
  const dispatch = useDispatch();

  // Active tab state
  const [activeTab, setActiveTab] = useState('all');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showClockModal, setShowClockModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ src: '', alt: '', type: '' });
  const [clockMode, setClockMode] = useState('clockIn'); // 'clockIn' or 'clockOut'
  const [selectedItem, setSelectedItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // Number of items per page

  // Employee filter search states
  const [employeeFilterSearch, setEmployeeFilterSearch] = useState('');
  const [showEmployeeFilterDropdown, setShowEmployeeFilterDropdown] = useState(false);
  const employeeFilterToggleRef = useRef(null);
  const [employeeFilterDropdownPos, setEmployeeFilterDropdownPos] = useState(null);

  // Employee search states
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSearchResults, setEmployeeSearchResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Table expansion states
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Helper functions for formatting
  const formatDate = (dateString) => {
    if (!dateString) return '-';

    // Handle dd/mm/yyyy format from server
    if (typeof dateString === 'string' && dateString.includes('/')) {
      // Already in dd/mm/yyyy format, return as is
      return dateString;
    }

    // Handle other date formats - convert to dd/mm/yyyy
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.warn('Invalid date format:', dateString);
      return 'Invalid Date';
    }
  };

  // Helper function to format date for HTML date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    try {
      let date;

      // Handle dd/mm/yyyy format from server
      if (typeof dateString === 'string' && dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return '';

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.warn('Invalid date format for input:', dateString);
      return '';
    }
  };

  const formatWeekday = (dateString) => {
    if (!dateString) return '';

    let date;
    // Handle dd/mm/yyyy format from server
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      date = new Date(`${year}-${month}-${day}`);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Get user data from Redux store
  const user = useSelector((state) => state.auth.user);
  const userRole = user?.role;
  const isUserAdmin = isAdmin(userRole);
  const isUserEmployee = userRole === USER_ROLES.EMPLOYEE;

  // RTK Query hooks - conditionally use different queries based on role
  const adminAttendanceQuery = useGetAttendanceQuery({
    page: currentPage,
    limit: itemsPerPage,
    employeeId: employeeFilter || undefined,
    status: statusFilter || undefined,
    startDate: startDateFilter || undefined,
    endDate: endDateFilter || undefined,
    companyId: user?.companyId || undefined,
    tenantId: user?.tenantId || undefined,
  }, {
    skip: isUserEmployee, // Skip admin query for employees
  });

  const employeeAttendanceQuery = useGetMyAttendanceQuery({}, {
    skip: !isUserEmployee, // Skip employee query for admins
  });

  // Use appropriate query based on role
  const {
    data: attendanceData,
    isLoading: attendanceLoading,
    error: attendanceError,
    refetch: refetchAttendance,
  } = isUserEmployee ? employeeAttendanceQuery : adminAttendanceQuery;

  // Get employees for filter dropdown
  const { data: employeesData, error: employeesError, isLoading: employeesLoading } = useGetEmployeesQuery({
    companyId: user?.companyId,
    limit: 1000 // Get all employees for dropdown
  });

  // Get branches for filter dropdown
  const { data: branchesData, error: branchesError, isLoading: branchesLoading } = useGetBranchesQuery({
    companyId: user?.companyId,
    limit: 1000 // Get all branches for dropdown
  });

  // Get shifts for filter dropdown
  const { data: shiftsData, error: shiftsError, isLoading: shiftsLoading } = useGetShiftsQuery({
    companyId: user?.companyId,
    limit: 1000 // Get all shifts for dropdown
  });

  // Get departments for employee details
  const { data: departmentsData, error: departmentsError, isLoading: departmentsLoading } = useGetDepartmentsQuery({
    companyId: user?.companyId,
    limit: 1000 // Get all departments for dropdown
  });

  // Get designations for employee details
  const { data: designationsData, error: designationsError, isLoading: designationsLoading } = useGetDesignationsQuery({
    companyId: user?.companyId,
    limit: 1000 // Get all designations for dropdown
  });

  // Debug: Log API errors if they occur
  useEffect(() => {
    if (employeesError) {
      console.warn('Employees API Error:', employeesError);
      console.log('Employees error details:', {
        status: employeesError.status,
        data: employeesError.data,
        error: employeesError.error
      });
    }
    if (branchesError) {
      console.warn('Branches API Error:', branchesError);
    }
    if (shiftsError) {
      console.warn('Shifts API Error:', shiftsError);
    }
    if (departmentsError) {
      console.warn('Departments API Error:', departmentsError);
    }
    if (designationsError) {
      console.warn('Designations API Error:', designationsError);
    }

    // Debug user object
    console.log('Current user object:', user);
    console.log('User companyId:', user?.companyId);
    console.log('User tenantId:', user?.tenantId);

    // Debug API responses
    console.log('Employees data:', employeesData);
    console.log('Employees loading:', employeesLoading);
    console.log('Departments data:', departmentsData);
    console.log('Designations data:', designationsData);
  }, [employeesError, branchesError, shiftsError, departmentsError, designationsError, user, employeesData, employeesLoading, departmentsData, designationsData]);

  // Normalize employees data shape and provide robust search filtering
  // Support multiple API shapes:
  // - employeesData is an array
  // - employeesData.data is an array
  // - employeesData.data.employees is an array (server uses nested data.employees)
  let employeesList = [];
  if (Array.isArray(employeesData)) {
    employeesList = employeesData;
  } else if (employeesData && Array.isArray(employeesData.data)) {
    employeesList = employeesData.data;
  } else if (employeesData && employeesData.data && Array.isArray(employeesData.data.employees)) {
    employeesList = employeesData.data.employees;
  } else {
    employeesList = [];
  }

  useEffect(() => {
    // Defensive logging
    console.log('Normalized employeesList length:', employeesList.length);

    if (!Array.isArray(employeesList) || employeesList.length === 0) {
      setEmployeeSearchResults([]);
      return;
    }

    if (employeeSearchTerm.trim() === '') {
      setEmployeeSearchResults(employeesList.slice(0, 50)); // Show first 50 by default
    } else {
      const q = employeeSearchTerm.toLowerCase();
      const filtered = employeesList.filter(employee =>
        (employee?.firstName || '').toLowerCase().includes(q) ||
        (employee?.lastName || '').toLowerCase().includes(q) ||
        (employee?.employeeId || '').toLowerCase().includes(q) ||
        `${(employee?.firstName || '')} ${(employee?.lastName || '')}`.toLowerCase().includes(q)
      );
      setEmployeeSearchResults(filtered.slice(0, 50)); // Limit to 50 results for performance
    }
  }, [employeesData, employeeSearchTerm]);

  // Ensure selectedEmployee is set in edit mode when employees load
  useEffect(() => {
    if (showEditModal && formData.employeeId && !selectedEmployee && Array.isArray(employeesList) && employeesList.length > 0) {
      const employeeId = typeof formData.employeeId === 'object' ? formData.employeeId._id : formData.employeeId;
      const employee = employeesList.find(emp => emp._id === employeeId);
      if (employee) {
        setSelectedEmployee(employee);
        setEmployeeSearchTerm(`${employee.firstName || ''} ${employee.lastName || ''} (${employee.employeeId || ''})`.trim());
      }
    }
  }, [employeesList, formData.employeeId, showEditModal, selectedEmployee]);

  // Get company settings
  const { data: companyData } = useGetMyCompanyQuery();
  const attendanceType = companyData?.data?.settings?.attendance?.type || 'system';
  const selfieRequired = attendanceType === 'selfie';

  // Mutation hooks
  const [createAttendance] = useCreateAttendanceMutation();
  const [updateAttendance] = useUpdateAttendanceMutation();
  const [deleteAttendance] = useDeleteAttendanceMutation();
  const [clockIn] = useClockInMutation();
  const [clockOut] = useClockOutMutation();

  // Update Redux state when data changes
  useEffect(() => {
    if (attendanceData && attendanceData.data && Array.isArray(attendanceData.data)) {
      dispatch(setAttendance(attendanceData.data));
    } else if (attendanceData && Array.isArray(attendanceData)) {
      // Fallback for direct array response
      dispatch(setAttendance(attendanceData));
    }
  }, [attendanceData, dispatch]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [employeeFilter, statusFilter, startDateFilter, endDateFilter, searchTerm]);

  // Close employee filter dropdown when clicking outside; keep portal in mind and reposition on scroll/resize
  useEffect(() => {
    if (!showEmployeeFilterDropdown) return undefined;

    const handleDocClick = (event) => {
      const target = event.target;
      // Click inside the toggle button
      if (employeeFilterToggleRef.current && employeeFilterToggleRef.current.contains(target)) return;
      // Click inside the portal dropdown
      const portalEl = document.getElementById('employee-filter-portal');
      if (portalEl && portalEl.contains(target)) return;

      setShowEmployeeFilterDropdown(false);
      setEmployeeFilterSearch('');
    };

    const handleScrollResize = () => {
      if (employeeFilterToggleRef.current) {
        const rect = employeeFilterToggleRef.current.getBoundingClientRect();
        setEmployeeFilterDropdownPos(rect);
      }
    };

    document.addEventListener('mousedown', handleDocClick);
    window.addEventListener('scroll', handleScrollResize, true);
    window.addEventListener('resize', handleScrollResize);

    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      window.removeEventListener('scroll', handleScrollResize, true);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [showEmployeeFilterDropdown]);

  // Keyboard handler for image modal
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape' && showImageModal) {
        setShowImageModal(false);
      }
    };

    if (showImageModal) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'unset'; // Restore scroll
    };
  }, [showImageModal]);

  // Get data from Redux store
  const attendance = useSelector((state) => state.attendance.attendance || []);

  // Ensure attendance is always an array
  const attendanceArray = Array.isArray(attendance) ? attendance : [];

  // Calculate attendance status counts with proper filtering
  const getStatusCount = (status) => {
    if (!attendanceArray || attendanceArray.length === 0) return 0;
    
    if (status === 'all') return attendanceArray.length;
    
    // Handle multiple status variations for more accurate counting
    switch (status) {
      case 'present':
        return attendanceArray.filter(item => 
          item.status === 'present' || item.status === 'on_time'
        ).length;
      case 'absent':
        return attendanceArray.filter(item => 
          item.status === 'absent' || item.status === 'no_show'
        ).length;
      case 'late':
        return attendanceArray.filter(item => 
          item.status === 'late' || item.status === 'late_arrival'
        ).length;
      case 'half_day':
        return attendanceArray.filter(item => 
          item.status === 'half_day' || item.status === 'partial'
        ).length;
      case 'overtime':
        return attendanceArray.filter(item => 
          item.status === 'overtime' || (item.overtimeMinutes && item.overtimeMinutes > 0)
        ).length;
      case 'early_leave':
        return attendanceArray.filter(item => 
          item.status === 'early_leave' || item.earlyLeaveMinutes > 0
        ).length;
      default:
        return attendanceArray.filter(item => item.status === status).length;
    }
  };

  // Calculate total pages
  const totalItems = attendanceData?.pagination?.totalRecords || attendanceArray.length;
  const totalPages = attendanceData?.pagination?.totalPages || Math.ceil(totalItems / itemsPerPage);

  // Tab configuration with proper data filtering
  const tabs = [
    {
      id: 'all',
      name: t('all', { defaultValue: 'All' }),
      icon: UserGroupIcon,
      color: 'blue',
      gradient: 'from-blue-400 to-blue-500',
      data: attendanceArray,
      count: getStatusCount('all'),
      loading: attendanceLoading,
      error: attendanceError,
    },
    {
      id: 'present',
      name: t('present', { defaultValue: 'Present' }),
      icon: CheckCircleIcon,
      color: 'green',
      gradient: 'from-emerald-400 to-green-500',
      data: attendanceArray.filter(item => 
        item.status === 'present' || item.status === 'on_time'
      ),
      count: getStatusCount('present'),
      loading: attendanceLoading,
      error: attendanceError,
    },
    {
      id: 'absent',
      name: t('absent', { defaultValue: 'Absent' }),
      icon: XCircleIcon,
      color: 'red',
      gradient: 'from-red-400 to-red-500',
      data: attendanceArray.filter(item => 
        item.status === 'absent' || item.status === 'no_show'
      ),
      count: getStatusCount('absent'),
      loading: attendanceLoading,
      error: attendanceError,
    },
    {
      id: 'late',
      name: t('late', { defaultValue: 'Late' }),
      icon: ClockOutlineIcon,
      color: 'yellow',
      gradient: 'from-yellow-400 to-orange-500',
      data: attendanceArray.filter(item => 
        item.status === 'late' || item.status === 'late_arrival'
      ),
      count: getStatusCount('late'),
      loading: attendanceLoading,
      error: attendanceError,
    },
    {
      id: 'overtime',
      name: t('overtime', { defaultValue: 'Overtime' }),
      icon: ClockIcon,
      color: 'purple',
      gradient: 'from-purple-400 to-pink-500',
      data: attendanceArray.filter(item => 
        item.status === 'overtime' || (item.overtimeMinutes && item.overtimeMinutes > 0)
      ),
      count: getStatusCount('overtime'),
      loading: attendanceLoading,
      error: attendanceError,
    },
  ];

  const activeTabData = tabs.find((tab) => tab.id === activeTab);

  // Helper functions to get branch and shift names
  const getBranchName = (branchId) => {
    if (!branchId) return 'Main Branch';
    if (typeof branchId === 'object') {
      return branchId?.name || branchId?.branchName || 'Main Branch';
    }
    // Find branch by ID
    const branch = branchesData?.data?.find(b => b._id === branchId);
    return branch?.name || branch?.branchName || 'Main Branch';
  };

  const getShiftName = (shiftId) => {
    if (Array.isArray(shiftId)) {
      const names = shiftId.map(id => getShiftName(id)).filter(Boolean);
      return names.length > 0 ? names.join(', ') : 'Default Shift';
    }
    if (!shiftId) return 'Default Shift';
    if (typeof shiftId === 'object') {
      return shiftId?.name || shiftId?.shiftName || 'Default Shift';
    }
    // Find shift by ID
    const shift = shiftsData?.data?.find(s => s._id === shiftId);
    return shift?.name || shift?.shiftName || 'Default Shift';
  };

  const getDepartmentName = (departmentId) => {
    if (!departmentId) return 'N/A';
    if (typeof departmentId === 'object') {
      return departmentId?.name || departmentId?.departmentName || 'N/A';
    }
    // Find department by ID
    const department = departmentsData?.data?.find(d => d._id === departmentId);
    return department?.name || department?.departmentName || 'N/A';
  };

  const getDesignationName = (designationId) => {
    if (!designationId) return 'N/A';
    if (typeof designationId === 'object') {
      return designationId?.name || designationId?.designationName || 'N/A';
    }
    // Find designation by ID
    const designation = designationsData?.data?.find(d => d._id === designationId);
    return designation?.name || designation?.designationName || 'N/A';
  };

  const getSelectedEmployeeName = (employeeId) => {
    if (!employeeId) return t('allEmployees', { defaultValue: 'All Employees' });
    const employee = employeesList?.find(emp => emp._id === employeeId);
    if (employee) {
      return `${employee.firstName} ${employee.lastName} (${employee.employeeId})`;
    }
    return t('allEmployees', { defaultValue: 'All Employees' });
  };

  const getShiftTimes = (shiftId) => {
    if (Array.isArray(shiftId)) {
      return { startTime: 'Multiple', endTime: 'Shifts' };
    }
    if (!shiftId) return { startTime: 'N/A', endTime: 'N/A' };
    if (typeof shiftId === 'object') {
      return {
        startTime: shiftId?.startTime || 'N/A',
        endTime: shiftId?.endTime || 'N/A'
      };
    }
    // Find shift by ID
    const shift = shiftsData?.data?.find(s => s._id === shiftId);
    return {
      startTime: shift?.startTime || 'N/A',
      endTime: shift?.endTime || 'N/A'
    };
  };

  // Form handlers
  const handleAdd = () => {
    const initialData = getInitialFormData();
    setFormData(initialData);
    setShowAddModal(true);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowClockModal(false);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    // Transform the item data to match form expectations
    const formData = {
      ...item,
      employeeId: typeof item.employeeId === 'object' ? item.employeeId?._id || item.employeeId : item.employeeId,
      // Format date for the date input field (YYYY-MM-DD)
      date: formatDateForInput(item.date),
      // Get branchId and shiftId from the employee data, not the attendance record
      branchId: item.employeeId?.branch || item.branchId,
      shiftId: item.shiftId?._id || item.shiftId || item.employeeId?.shiftId?._id || item.employeeId?.shiftId || (Array.isArray(item.employeeId?.shiftIds) ? item.employeeId.shiftIds[0] : ''),
      departmentId: item.employeeId?.department || item.departmentId,
      designationId: item.employeeId?.designation || item.designationId,
      clockInDevice: item.clockInDevice || {
        type: 'web',
        deviceId: '',
        ipAddress: '',
        userAgent: navigator.userAgent
      },
      clockOutDevice: item.clockOutDevice || {
        type: 'web',
        deviceId: '',
        ipAddress: '',
        userAgent: navigator.userAgent
      },
      clockInLocation: item.clockInLocation || {
        latitude: null,
        longitude: null,
        address: '',
        accuracy: null
      },
      clockOutLocation: item.clockOutLocation || {
        latitude: null,
        longitude: null,
        address: '',
        accuracy: null
      }
    };
    setFormData(formData);

    // Auto-fill employee selection - always try to find from employeesList first
    const employeeId = typeof item.employeeId === 'object' ? item.employeeId._id : item.employeeId;
    const employee = employeesList.find(emp => emp._id === employeeId);
    if (employee) {
      setSelectedEmployee(employee);
      setEmployeeSearchTerm(`${employee.firstName || ''} ${employee.lastName || ''} (${employee.employeeId || ''})`.trim());
    } else if (typeof item.employeeId === 'object') {
      // Fallback to the object from the attendance record
      setSelectedEmployee(item.employeeId);
      setEmployeeSearchTerm(`${item.employeeId.firstName || ''} ${item.employeeId.lastName || ''} (${item.employeeId.employeeId || ''})`.trim());
    } else {
      // If employeesList is not loaded yet, selectedEmployee will be set by useEffect when it loads
      setSelectedEmployee(null);
      setEmployeeSearchTerm('');
    }

    setShowAddModal(false);
    setShowEditModal(true);
    setShowDeleteModal(false);
    setShowClockModal(false);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowViewModal(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleClockIn = async () => {
    setClockMode('clockIn');
    setShowClockModal(true);
  };

  const handleClockOut = async () => {
    setClockMode('clockOut');
    setShowClockModal(true);
  };

  // Employee search handlers
  const handleEmployeeSearchChange = (e) => {
    setEmployeeSearchTerm(e.target.value);
    setShowEmployeeDropdown(true);
    setHighlightedIndex(-1);
  };

  const handleEmployeeSelect = (employee) => {
    const employeeShiftIds = Array.isArray(employee.shiftIds) && employee.shiftIds.length > 0
      ? employee.shiftIds
      : (employee.shiftId ? [employee.shiftId] : []);

    setSelectedEmployee(employee);
    setFormData({
      ...formData,
      employeeId: employee._id,
      branchId: employee.branch || '',
      departmentId: employee.department || '',
      designationId: employee.designation || '',
      shiftId: employeeShiftIds[0] || employee.shiftId || ''
    });
    setEmployeeSearchTerm(`${employee.firstName} ${employee.lastName} (${employee.employeeId})`);
    setShowEmployeeDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleEmployeeKeyDown = (e) => {
    if (!showEmployeeDropdown || !Array.isArray(employeeSearchResults) || employeeSearchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < employeeSearchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : employeeSearchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < employeeSearchResults.length) {
          handleEmployeeSelect(employeeSearchResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowEmployeeDropdown(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleEmployeeInputFocus = () => {
    setShowEmployeeDropdown(true);
    if (employeeSearchTerm === '' && employeesList && employeesList.length > 0) {
      setEmployeeSearchResults(employeesList.slice(0, 50));
    } else if (employeeSearchTerm === '') {
      // If no data available, show empty results
      setEmployeeSearchResults([]);
    }
  };

  const handleEmployeeInputBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => {
      setShowEmployeeDropdown(false);
      setHighlightedIndex(-1);
    }, 200);
  };

  const clearEmployeeSelection = () => {
    setSelectedEmployee(null);
    setFormData({ ...formData, employeeId: '' });
    setEmployeeSearchTerm('');
    setShowEmployeeDropdown(false);
    setHighlightedIndex(-1);
  };

  // Table expansion functions
  const toggleRowExpansion = (itemId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(itemId)) {
      newExpandedRows.delete(itemId);
    } else {
      newExpandedRows.add(itemId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.preventDefault();

    // Basic validation
    if (!formData.employeeId) {
      toast.error('Please select an employee');
      return;
    }

    if (!formData.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      // Prepare the data for submission
      const submissionData = {
        ...formData,
        // Ensure IDs are strings, not objects
        employeeId: typeof formData.employeeId === 'object' ? formData.employeeId?._id : formData.employeeId,
        // Remove branchId and shiftId as they should be derived from the employee
        // branchId: typeof formData.branchId === 'object' ? formData.branchId?._id : formData.branchId,
        // shiftId: typeof formData.shiftId === 'object' ? formData.shiftId?._id : formData.shiftId,
        // Convert time strings to proper format if needed
      };

      if (showAddModal) {
        await createAttendance(submissionData).unwrap();
        toast.success('Attendance record created successfully');
        refetchAttendance();
      } else if (showEditModal) {
        await updateAttendance({ id: selectedItem._id, ...submissionData }).unwrap();
        toast.success('Attendance record updated successfully');
        refetchAttendance();
      }
      closeModals();
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error?.data?.message ||
                          error?.message ||
                          'Operation failed. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteAttendance(selectedItem._id).unwrap();
      toast.success(t('attendanceDeletedSuccessfully', { defaultValue: 'Attendance record deleted successfully' }));
      refetchAttendance();
      closeModals();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMessage = error?.data?.message ||
                          error?.message ||
                          t('deleteFailed', { defaultValue: 'Failed to delete attendance record. Please try again.' });
      toast.error(errorMessage);
    }
  };

  const closeModals = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowClockModal(false);
    setSelectedItem(null);
    setFormData({});
    // Reset employee search state
    setEmployeeSearchTerm('');
    setSelectedEmployee(null);
    setShowEmployeeDropdown(false);
    setHighlightedIndex(-1);
  };

  const getInitialFormData = () => {
    const initialData = {
      employeeId: '',
      date: new Date().toISOString().split('T')[0],
      status: 'present',
      actualInTime: '',
      actualOutTime: '',
      workMinutes: 0,
      breakMinutes: 0,
      overtimeMinutes: 0,
      lateMinutes: 0,
      earlyLeaveMinutes: 0,
      branchId: '',
      shiftId: '',
      clockInDevice: {
        type: 'web',
        deviceId: '',
        ipAddress: '',
        userAgent: navigator.userAgent
      },
      clockOutDevice: {
        type: 'web',
        deviceId: '',
        ipAddress: '',
        userAgent: navigator.userAgent
      },
      clockInLocation: {
        latitude: null,
        longitude: null,
        address: '',
        accuracy: null
      },
      clockOutLocation: {
        latitude: null,
        longitude: null,
        address: '',
        accuracy: null
      },
      remarks: '',
      companyId: user?.companyId || 'default-company-id',
      tenantId: user?.tenantId || 'default-tenant-id',
    };
    return initialData;
  };

  const renderForm = () => {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Employee Selection Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <UserIcon className="h-6 w-6 mr-3 text-blue-600" />
            Select Employee
          </h3>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Employee *
            </label>
            <div className="relative">
              <input
                type="text"
                value={employeeSearchTerm}
                onChange={handleEmployeeSearchChange}
                onKeyDown={handleEmployeeKeyDown}
                onFocus={handleEmployeeInputFocus}
                onBlur={handleEmployeeInputBlur}
                placeholder="Type employee name or ID..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-12 text-lg"
                required
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                {employeeSearchTerm && (
                  <button
                    type="button"
                    onClick={clearEmployeeSelection}
                    className="p-1 hover:bg-gray-100 rounded-full mr-2"
                  >
                    <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
                <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
              </div>
            </div>

            {/* Employee Dropdown */}
            {showEmployeeDropdown && (
              <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                {employeesLoading ? (
                  <div className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <ArrowPathIcon className="h-5 w-5 animate-spin mr-3" />
                      Loading employees...
                    </div>
                  </div>
                ) : employeesError ? (
                  <div className="px-6 py-4 text-sm text-red-500">
                    Error loading employees
                  </div>
                ) : !Array.isArray(employeeSearchResults) || employeeSearchResults.length === 0 ? (
                  <div className="px-6 py-4 text-sm text-gray-500">
                    No employees found
                  </div>
                ) : (
                  employeeSearchResults.map((employee, index) => (
                    <div
                      key={employee._id}
                      onClick={() => handleEmployeeSelect(employee)}
                      className={`px-6 py-4 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-blue-50 transition-colors duration-150 ${
                        index === highlightedIndex ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold mr-4">
                          {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {employee.firstName} {employee.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {employee.employeeId}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Selected Employee Card */}
        {selectedEmployee && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-6">
                  {selectedEmployee.firstName?.charAt(0)}{selectedEmployee.lastName?.charAt(0)}
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-800">
                    {selectedEmployee.firstName} {selectedEmployee.lastName}
                  </h4>
                  <p className="text-gray-600">Employee ID: {selectedEmployee.employeeId}</p>
                  <div className="flex items-center mt-2 space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                      {getBranchName(selectedEmployee.branch)}
                    </span>
                    <span className="flex items-center">
                      <BriefcaseIcon className="h-4 w-4 mr-1" />
                      {getShiftName(selectedEmployee.shiftIds?.length ? selectedEmployee.shiftIds : selectedEmployee.shiftId)}
                    </span>
                  </div>
                  <div className="flex items-center mt-1 text-sm text-gray-500">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    Shift Time: {(() => {
                      const shiftTimes = getShiftTimes(selectedEmployee.shiftIds?.length ? selectedEmployee.shiftIds : selectedEmployee.shiftId);
                      return shiftTimes.startTime !== 'N/A' && shiftTimes.endTime !== 'N/A'
                        ? `${shiftTimes.startTime} - ${shiftTimes.endTime}`
                        : 'Not set';
                    })()}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">Department</div>
                <div className="font-medium text-gray-800">
      
                  {getDepartmentName(selectedEmployee.department)}
                </div>
                <div className="text-sm text-gray-500 mt-1">Designation</div>
                <div className="font-medium text-gray-800">
                  {getDesignationName(selectedEmployee.designation)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Attendance Details Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
          <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <CalendarIcon className="h-6 w-6 mr-3 text-purple-600" />
            Attendance Details
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Date */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                required
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Status *
              </label>
              <select
                value={formData.status || 'present'}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
              >
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="half_day">Half Day</option>
                <option value="late">Late</option>
                <option value="early_leave">Early Leave</option>
                <option value="overtime">Overtime</option>
                <option value="holiday">Holiday</option>
                <option value="leave">Leave</option>
                <option value="weekend">Weekend</option>
              </select>
            </div>

            {/* Shift */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Shift
              </label>
              <select
                value={formData.shiftId || ''}
                onChange={(e) => setFormData({ ...formData, shiftId: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                disabled={!shiftsData?.data || shiftsData.data.length === 0}
              >
                <option value="">Select shift</option>
                {(Array.isArray(shiftsData?.data) ? shiftsData.data : [])
                  .filter(shift => {
                    if (selectedEmployee?.shiftIds?.length > 0) {
                      return selectedEmployee.shiftIds.includes(shift._id);
                    }
                    if (selectedEmployee?.shiftId) {
                      const selectedShiftId = typeof selectedEmployee.shiftId === 'object'
                        ? selectedEmployee.shiftId._id
                        : selectedEmployee.shiftId;
                      return shift._id === selectedShiftId;
                    }
                    return true;
                  })
                  .map((shift) => (
                    <option key={shift._id} value={shift._id}>
                      {shift.name} ({shift.startTime} - {shift.endTime})
                    </option>
                  ))}
              </select>
            </div>

            {/* Clock In Time */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Clock In Time
              </label>
              <input
                type="time"
                value={formData.actualInTime ? new Date(formData.actualInTime).toTimeString().slice(0, 5) : ''}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  if (timeValue) {
                    // Create local date and time to preserve local timezone
                    const [hours, minutes] = timeValue.split(':');
                    const localDate = new Date(formData.date);
                    const localDateTime = new Date(
                      localDate.getFullYear(),
                      localDate.getMonth(),
                      localDate.getDate(),
                      parseInt(hours, 10),
                      parseInt(minutes, 10)
                    );
                    // Send as ISO string to preserve local time
                    setFormData({ ...formData, actualInTime: localDateTime.toISOString() });
                  } else {
                    setFormData({ ...formData, actualInTime: '' });
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
              />
            </div>

            {/* Clock Out Time */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Clock Out Time
              </label>
              <input
                type="time"
                value={formData.actualOutTime ? new Date(formData.actualOutTime).toTimeString().slice(0, 5) : ''}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  if (timeValue) {
                    // Create local date and time to preserve local timezone
                    const [hours, minutes] = timeValue.split(':');
                    const localDate = new Date(formData.date);
                    const localDateTime = new Date(
                      localDate.getFullYear(),
                      localDate.getMonth(),
                      localDate.getDate(),
                      parseInt(hours, 10),
                      parseInt(minutes, 10)
                    );
                    // Send as ISO string to preserve local time
                    setFormData({ ...formData, actualOutTime: localDateTime.toISOString() });
                  } else {
                    setFormData({ ...formData, actualOutTime: '' });
                  }
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
              />
            </div>

            {/* Work Hours */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Work Hours (minutes)
              </label>
              <input
                type="number"
                value={formData.workMinutes || 0}
                onChange={(e) => setFormData({ ...formData, workMinutes: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                min="0"
              />
            </div>

            {/* Break Minutes */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Break Minutes
              </label>
              <input
                type="number"
                value={formData.breakMinutes || 0}
                onChange={(e) => setFormData({ ...formData, breakMinutes: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
                min="0"
              />
            </div>
          </div>

          {/* Remarks */}
          <div className="mt-6 space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Remarks
            </label>
            <textarea
              value={formData.remarks || ''}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-lg"
              placeholder="Add any additional notes..."
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              {showAddModal ? 'Create Attendance' : 'Update Attendance'}
            </div>
          </button>
        </div>
      </div>
    );
  };

  const calculateWorkHours = () => {
    if (!formData.clockInTime || !formData.clockOutTime) return '0.00';

    const clockIn = new Date(`2000-01-01T${formData.clockInTime}`);
    const clockOut = new Date(`2000-01-01T${formData.clockOutTime}`);
    const breakTime = parseFloat(formData.breakTime || 0);

    let hours = (clockOut - clockIn) / (1000 * 60 * 60);
    hours = Math.max(0, hours - breakTime);

    return hours.toFixed(2);
  };

  const renderTable = () => {
    if (!activeTabData?.data) return null;

    // Filter data based on search term
    const filteredData = activeTabData.data.filter((item) => {
      if (!searchTerm) return true;

      const searchLower = searchTerm.toLowerCase();
      const employeeName = item.employeeId?.firstName && item.employeeId?.lastName
        ? `${item.employeeId.firstName} ${item.employeeId.lastName}`.toLowerCase()
        : '';
      const employeeId = item.employeeId?.employeeId?.toLowerCase() || '';

      return (
        employeeName.includes(searchLower) ||
        employeeId.includes(searchLower) ||
        item.status?.toLowerCase().includes(searchLower) ||
        item.date?.includes(searchLower)
      );
    });

    // Empty state
    if (filteredData.length === 0) {
      return (
        <div className="text-center py-20">
          <div className="mx-auto w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-600/20 backdrop-blur-xl rounded-3xl flex items-center justify-center mb-6 border border-white/20 shadow-2xl">
            <ClockIcon className="h-16 w-16 text-blue-500" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">
            {searchTerm ? `No attendance records found matching "${searchTerm}"` : t('noAttendanceFound', { defaultValue: 'No attendance records found' })}
          </h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            {searchTerm
              ? 'Try adjusting your search terms or clear the search to see all records.'
              : t('getStartedAttendance', { defaultValue: 'Get started by adding your first attendance record.' })
            }
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-gray-600 hover:to-gray-700 mr-4"
            >
              <XMarkIcon className="h-5 w-5 mr-2" />
              Clear Search
            </button>
          )}
          {isUserAdmin && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-blue-600 hover:to-purple-700"
            >
              <PlusIcon className="h-5 w-5 mr-3" />
              Add Attendance Record
            </button>
          )}
        </div>
      );
    }

    const getStatusBadge = (status) => {
      const statusConfig = {
        present: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircleIcon },
        absent: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircleIcon },
        late: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: ClockOutlineIcon },
        half_day: { bg: 'bg-blue-100', text: 'text-blue-800', icon: UserIcon },
        overtime: { bg: 'bg-purple-100', text: 'text-purple-800', icon: ClockIcon },
        early_leave: { bg: 'bg-orange-100', text: 'text-orange-800', icon: ExclamationTriangleIcon },
        holiday: { bg: 'bg-cyan-100', text: 'text-cyan-800', icon: CalendarIcon },
        leave: { bg: 'bg-gray-100', text: 'text-gray-800', icon: BriefcaseIcon },
        weekend: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: CalendarDaysIcon }
      };

      const config = statusConfig[status] || statusConfig.present;
      const IconComponent = config.icon;

      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
          <IconComponent className="h-3 w-3 mr-1" />
          {status.replace('_', ' ')}
        </span>
      );
    };

    const formatTime = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const formatDuration = (minutes) => {
      if (!minutes || minutes === 0) return '-';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    return (
      <div className="space-y-4">
        {/* Desktop Table View */}
        <div className="hidden lg:block">
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-xl overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <table className="min-w-full divide-y divide-gray-200/60">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100/80">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60 w-12">
                      {/* Expand column */}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60">
                      Employee
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60">
                      Clock In/Out
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60">
                      Work Time
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider border-b border-gray-200/60">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {Array.isArray(filteredData) && filteredData.map((item, index) => {
                    const employeeName = item.employeeId?.firstName && item.employeeId?.lastName
                      ? `${item.employeeId.firstName} ${item.employeeId.lastName}`
                      : 'Unknown Employee';
                    const employeeId = item.employeeId?.employeeId || 'N/A';
                    const isExpanded = expandedRows.has(item._id);

                    return (
                      <React.Fragment key={item._id}>
                        {/* Main Row */}
                        <tr className="hover:bg-gray-50/70 transition-colors duration-200 group">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              onClick={() => toggleRowExpansion(item._id)}
                              className="p-1 rounded-full hover:bg-gray-100 transition-colors duration-200"
                              title={isExpanded ? "Collapse details" : "Expand details"}
                            >
                              {isExpanded ? (
                                <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm">
                                  <UserIcon className="h-5 w-5 text-white" />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                  {employeeName}
                                </div>
                                <div className="text-sm text-gray-500 font-mono">
                                  ID: {employeeId}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatDate(item.date)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatWeekday(item.date)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(item.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="space-y-1">
                          <div className="flex items-center">
                            <PlayIcon className="h-3 w-3 mr-1 text-green-500" />
                            <span className="font-medium">In:</span>
                            <span className="ml-1">{formatTime(item.actualInTime)}</span>
                          </div>
                          <div className="flex items-center">
                            <StopIcon className="h-3 w-3 mr-1 text-red-500" />
                            <span className="font-medium">Out:</span>
                            <span className="ml-1">{formatTime(item.actualOutTime)}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatDuration(item.workMinutes)}
                        </div>
                        {item.overtimeMinutes > 0 && (
                          <div className="text-xs text-purple-600 font-medium">
                            +{formatDuration(item.overtimeMinutes)} OT
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleView(item);
                            }}
                            className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 hover:border-blue-300 transition-all duration-200 group-hover:shadow-sm"
                            title="View Details"
                          >
                            <EyeIcon className="h-4 w-4 text-blue-600" />
                          </button>
                          {isUserAdmin && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(item);
                                }}
                                className="p-2 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 hover:border-purple-300 transition-all duration-200 group-hover:shadow-sm"
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4 text-purple-600" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(item);
                                }}
                                className="p-2 bg-red-50 hover:bg-red-100 rounded-lg border border-red-200 hover:border-red-300 transition-all duration-200 group-hover:shadow-sm"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4 text-red-600" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {isExpanded && (
                      <tr className="bg-gray-50/50">
                        <td colSpan="7" className="px-6 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Scheduled vs Actual Times */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                <ClockIcon className="h-4 w-4 mr-2 text-blue-500" />
                                Time Details
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Scheduled In:</span>
                                  <span className="font-medium">{formatTime(item.scheduledInTime)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Actual In:</span>
                                  <span className="font-medium">{formatTime(item.actualInTime)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Scheduled Out:</span>
                                  <span className="font-medium">{formatTime(item.scheduledOutTime)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Actual Out:</span>
                                  <span className="font-medium">{formatTime(item.actualOutTime)}</span>
                                </div>
                              </div>
                            </div>

                            {/* Performance Metrics */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                <ChartBarIcon className="h-4 w-4 mr-2 text-green-500" />
                                Performance
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Work Time:</span>
                                  <span className="font-medium">{formatDuration(item.workMinutes)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Break Time:</span>
                                  <span className="font-medium">{formatDuration(item.breakMinutes)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Overtime:</span>
                                  <span className="font-medium text-purple-600">{formatDuration(item.overtimeMinutes)}</span>
                                </div>
                                {item.lateMinutes > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Late:</span>
                                    <span className="font-medium text-yellow-600">{formatDuration(item.lateMinutes)}</span>
                                  </div>
                                )}
                                {item.earlyLeaveMinutes > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Early Leave:</span>
                                    <span className="font-medium text-orange-600">{formatDuration(item.earlyLeaveMinutes)}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Device & Location Info */}
                            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                <DevicePhoneMobileIcon className="h-4 w-4 mr-2 text-indigo-500" />
                                Device & Location
                              </h4>
                              <div className="space-y-2 text-sm">
                                {item.clockInDevice && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Clock In Device:</span>
                                    <span className="font-medium capitalize">{item.clockInDevice.type}</span>
                                  </div>
                                )}
                                {item.clockOutDevice && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Clock Out Device:</span>
                                    <span className="font-medium capitalize">{item.clockOutDevice.type}</span>
                                  </div>
                                )}
                                {item.clockInLocation && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Clock In Location:</span>
                                    <span className="font-medium text-green-600">✓</span>
                                  </div>
                                )}
                                {item.clockOutLocation && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Clock Out Location:</span>
                                    <span className="font-medium text-red-600">✓</span>
                                  </div>
                                )}
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Branch:</span>
                                  <span className="font-medium">{getBranchName(item.branchId)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Shift:</span>
                                  <span className="font-medium">{getShiftName(item.shiftId)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {/* Mobile Card View */}
                    <div className="lg:hidden space-y-3">
                      {Array.isArray(filteredData) && filteredData.map((item, index) => {
                        const employeeName = item.employeeId?.firstName && item.employeeId?.lastName
                          ? `${item.employeeId.firstName} ${item.employeeId.lastName}`
                          : 'Unknown Employee';
                        const employeeId = item.employeeId?.employeeId || 'N/A';
                        
                        return (
                          <div key={item._id} className="bg-white/90 backdrop-blur-xl rounded-xl border border-gray-200/50 shadow-lg overflow-hidden">
                            <div className="p-4">
                              {/* Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center space-x-3">
                                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm">
                                    <UserIcon className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900">{employeeName}</div>
                                    <div className="text-xs text-gray-500 font-mono">ID: {employeeId}</div>
                                  </div>
                                </div>
                                {getStatusBadge(item.status)}
                              </div>
                              
                              {/* Details Grid */}
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <div className="text-gray-500 text-xs">Date</div>
                                  <div className="font-medium">{formatDate(item.date)}</div>
                                  <div className="text-xs text-gray-400">{formatWeekday(item.date)}</div>
                                </div>
                                <div>
                                  <div className="text-gray-500 text-xs">Work Time</div>
                                  <div className="font-medium">{formatDuration(item.workMinutes)}</div>
                                </div>
                                {item.actualInTime && (
                                  <div>
                                    <div className="text-gray-500 text-xs">Clock In</div>
                                    <div className="font-medium text-green-600">{formatTime(item.actualInTime)}</div>
                                  </div>
                                )}
                                {item.actualOutTime && (
                                  <div>
                                    <div className="text-gray-500 text-xs">Clock Out</div>
                                    <div className="font-medium text-red-600">{formatTime(item.actualOutTime)}</div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="mt-3 flex justify-end space-x-2">
                                <button
                                  onClick={() => handleView(item)}
                                  className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 transition-colors"
                                >
                                  View
                                </button>
                                {isUserAdmin && (
                                  <>
                                    <button
                                      onClick={() => handleEdit(item)}
                                      className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDelete(item)}
                                      className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 transition-colors"
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
              {t('attendanceManagement', { defaultValue: 'Attendance Management' })}
            </h1>
            <p className="mt-2 text-gray-600 text-lg">
              {isUserEmployee 
                ? t('yourAttendanceRecords', { defaultValue: 'View and track your attendance records' })
                : t('manageEmployeeAttendance', { defaultValue: 'Track and manage employee attendance records efficiently' })
              }
            </p>
          </div>
          <div className="mt-6 sm:mt-0 flex space-x-4">
           
            {isUserAdmin && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-orange-500 to-red-600 text-white font-bold rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 hover:from-orange-600 hover:to-red-700 text-lg"
              >
                <PlusIcon className="h-6 w-6 mr-3" />
                {t('manualAttendance', { defaultValue: 'Manual Attendance' })}
              </button>
            )}
          </div>
        </div>

        {/* Tabs with enhanced styling */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 mb-8">
          <div className="border-b border-white/30">
            <nav className="px-4 py-4 overflow-x-auto scrollbar-hide" aria-label="Tabs">
              <div className="flex space-x-2 sm:space-x-4 lg:space-x-8 min-w-max">
                {Array.isArray(tabs) && tabs.map((tab) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center py-2 px-3 sm:py-3 sm:px-4 rounded-xl sm:rounded-2xl font-semibold text-xs sm:text-sm transition-all duration-300 transform hover:scale-105 whitespace-nowrap ${
                        activeTab === tab.id
                          ? `bg-gradient-to-r ${tab.gradient} text-white shadow-lg`
                          : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                      }`}
                    >
                      <IconComponent className="h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3" />
                      <span className="hidden xs:inline sm:inline">{tab.name}</span>
                      <span className="xs:hidden sm:hidden">{tab.name.split(' ')[0]}</span>
                      {tab.data && (
                        <span className={`ml-2 sm:ml-3 inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-bold ${
                          activeTab === tab.id
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tab.data.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 lg:p-8">
          {/* Statistics Cards with Glassmorphism */}
          {activeTabData?.data && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
              <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br from-emerald-400/20 to-green-600/20 backdrop-blur-xl border border-white/20 shadow-xl lg:shadow-2xl p-3 sm:p-4 lg:p-6 transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-2 right-2 lg:top-4 lg:right-4 w-2 h-2 lg:w-3 lg:h-3 bg-emerald-400 rounded-full animate-pulse"></div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <p className="text-emerald-700 text-xs sm:text-sm font-semibold mb-1">{t('present', { defaultValue: 'Present' })}</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                      {getStatusCount('present')}
                    </p>
                    <p className="text-xs text-emerald-600 mt-1 hidden sm:block">On time</p>
                  </div>
                  <div className="mt-2 sm:mt-0 p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-emerald-400 to-green-500 rounded-xl lg:rounded-2xl shadow-lg self-center sm:self-auto">
                    <CheckCircleIcon className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 to-green-500"></div>
              </div>

              <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br from-red-400/20 to-red-600/20 backdrop-blur-xl border border-white/20 shadow-xl lg:shadow-2xl p-3 sm:p-4 lg:p-6 transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-2 right-2 lg:top-4 lg:right-4 w-2 h-2 lg:w-3 lg:h-3 bg-red-400 rounded-full animate-pulse"></div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <p className="text-red-700 text-xs sm:text-sm font-semibold mb-1">{t('absent', { defaultValue: 'Absent' })}</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                      {getStatusCount('absent')}
                    </p>
                    <p className="text-xs text-red-600 mt-1 hidden sm:block">Not present</p>
                  </div>
                  <div className="mt-2 sm:mt-0 p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-red-400 to-red-500 rounded-xl lg:rounded-2xl shadow-lg self-center sm:self-auto">
                    <XCircleIcon className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-400 to-red-500"></div>
              </div>

              <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br from-yellow-400/20 to-orange-600/20 backdrop-blur-xl border border-white/20 shadow-xl lg:shadow-2xl p-3 sm:p-4 lg:p-6 transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-2 right-2 lg:top-4 lg:right-4 w-2 h-2 lg:w-3 lg:h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <p className="text-yellow-700 text-xs sm:text-sm font-semibold mb-1">{t('late', { defaultValue: 'Late' })}</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                      {getStatusCount('late')}
                    </p>
                    <p className="text-xs text-yellow-600 mt-1 hidden sm:block">Arrived late</p>
                  </div>
                  <div className="mt-2 sm:mt-0 p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl lg:rounded-2xl shadow-lg self-center sm:self-auto">
                    <ClockOutlineIcon className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-orange-500"></div>
              </div>

              <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br from-blue-400/20 to-indigo-600/20 backdrop-blur-xl border border-white/20 shadow-xl lg:shadow-2xl p-3 sm:p-4 lg:p-6 transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-2 right-2 lg:top-4 lg:right-4 w-2 h-2 lg:w-3 lg:h-3 bg-blue-400 rounded-full animate-pulse"></div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <p className="text-blue-700 text-xs sm:text-sm font-semibold mb-1">{t('halfDay', { defaultValue: 'Half Day' })}</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                      {getStatusCount('half_day')}
                    </p>
                    <p className="text-xs text-blue-600 mt-1 hidden sm:block">Partial attendance</p>
                  </div>
                  <div className="mt-2 sm:mt-0 p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl lg:rounded-2xl shadow-lg self-center sm:self-auto">
                    <UserIcon className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
              </div>

              <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl bg-gradient-to-br from-purple-400/20 to-pink-600/20 backdrop-blur-xl border border-white/20 shadow-xl lg:shadow-2xl p-3 sm:p-4 lg:p-6 transform hover:scale-105 transition-all duration-300 col-span-2 sm:col-span-3 lg:col-span-1">
                <div className="absolute top-2 right-2 lg:top-4 lg:right-4 w-2 h-2 lg:w-3 lg:h-3 bg-purple-400 rounded-full animate-pulse"></div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <p className="text-purple-700 text-xs sm:text-sm font-semibold mb-1">{t('overtime', { defaultValue: 'Overtime' })}</p>
                    <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">
                      {getStatusCount('overtime')}
                    </p>
                    <p className="text-xs text-purple-600 mt-1 hidden sm:block">Extra hours</p>
                  </div>
                  <div className="mt-2 sm:mt-0 p-2 sm:p-3 lg:p-4 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl lg:rounded-2xl shadow-lg self-center sm:self-auto">
                    <ClockIcon className="h-4 w-4 sm:h-6 sm:w-6 lg:h-8 lg:w-8 text-white" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-purple-400 to-pink-500"></div>
              </div>
            </div>
          )}

          {/* Search and Filters */}
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
            <div className="flex-1 max-w-full sm:max-w-lg">
              <div className="relative">
                {/* Glassmorphism search container */}
                <div className="absolute inset-0 bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 shadow-xl"></div>

                {/* Search icon with glassmorphism */}
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-400/20 to-purple-600/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/20">
                    <MagnifyingGlassIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                  </div>
                </div>

                <input
                  type="text"
                  placeholder={`${t('search')} ${t('attendance', { defaultValue: 'attendance records' })}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="relative block w-full pl-12 sm:pl-16 pr-10 sm:pr-12 py-2.5 sm:py-3 bg-transparent border-0 rounded-xl sm:rounded-2xl focus:outline-none focus:ring-0 text-sm font-medium text-gray-800 placeholder-gray-500"
                />

                {/* Clear search button */}
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center"
                  >
                    <div className="p-1.5 sm:p-2 bg-red-500/20 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/20 hover:bg-red-500/30 transition-all duration-200">
                      <XMarkIcon className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
                    </div>
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between sm:justify-end space-x-3">
              {isUserAdmin && (
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="relative inline-flex items-center px-3 sm:px-4 py-2 sm:py-3 bg-white/80 backdrop-blur-xl border border-white/20 rounded-xl sm:rounded-2xl text-sm font-medium text-gray-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  <div className="p-1 bg-gradient-to-br from-purple-400/20 to-pink-600/20 backdrop-blur-sm rounded-md sm:rounded-lg border border-white/20 mr-2">
                    <FunnelIcon className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
                  </div>
                  <span className="hidden sm:inline">{t('filters')}</span>
                  <span className="sm:hidden">Filter</span>
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && isUserAdmin && (
            <div className="mb-4 sm:mb-6 p-4 sm:p-6 bg-white/80 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/20 shadow-xl">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    {t('employee', { defaultValue: 'Employee' })}
                  </label>
                  <div className="relative employee-filter-dropdown">
                    <button
                      type="button"
                      ref={employeeFilterToggleRef}
                      onClick={() => {
                        const willOpen = !showEmployeeFilterDropdown;
                        if (willOpen && employeeFilterToggleRef.current) {
                          const rect = employeeFilterToggleRef.current.getBoundingClientRect();
                          setEmployeeFilterDropdownPos(rect);
                        }
                        setShowEmployeeFilterDropdown(willOpen);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-left flex items-center justify-between text-sm"
                    >
                      <span className="truncate">
                        {getSelectedEmployeeName(employeeFilter)}
                      </span>
                      <ChevronDownIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
                    </button>

                    {showEmployeeFilterDropdown && employeeFilterDropdownPos && createPortal(
                      <div
                        id="employee-filter-portal"
                        className="bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden"
                        style={{
                          position: 'absolute',
                          top: `${employeeFilterDropdownPos.bottom + window.scrollY + 6}px`,
                          left: `${employeeFilterDropdownPos.left + window.scrollX}px`,
                          width: `${employeeFilterDropdownPos.width}px`,
                          zIndex: 9999
                        }}
                      >
                        {/* Search input */}
                        <div className="p-2 border-b border-gray-200">
                          <div className="relative">
                            <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              placeholder={t('searchEmployees', { defaultValue: 'Search employees...' })}
                              value={employeeFilterSearch}
                              onChange={(e) => setEmployeeFilterSearch(e.target.value)}
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </div>

                        {/* Employee list */}
                        <div className="max-h-48 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => {
                              setEmployeeFilter('');
                              setEmployeeFilterSearch('');
                              setShowEmployeeFilterDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                          >
                            {t('allEmployees', { defaultValue: 'All Employees' })}
                          </button>

                          {employeesError ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Error loading employees
                            </div>
                          ) : employeesLoading ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              Loading employees...
                            </div>
                          ) : employeesList && employeesList.length > 0 ? (
                            employeesList
                              .filter(employee => {
                                if (!employeeFilterSearch.trim()) return true;
                                const searchTerm = employeeFilterSearch.toLowerCase();
                                return (
                                  (employee?.firstName || '').toLowerCase().includes(searchTerm) ||
                                  (employee?.lastName || '').toLowerCase().includes(searchTerm) ||
                                  (employee?.employeeId || '').toLowerCase().includes(searchTerm) ||
                                  `${(employee?.firstName || '')} ${(employee?.lastName || '')}`.toLowerCase().includes(searchTerm)
                                );
                              })
                              .map((employee) => (
                                <button
                                  key={employee._id}
                                  type="button"
                                  onClick={() => {
                                    setEmployeeFilter(employee._id);
                                    setEmployeeFilterSearch('');
                                    setShowEmployeeFilterDropdown(false);
                                  }}
                                  className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 text-sm"
                                >
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3">
                                      <UserIcon className="h-4 w-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 truncate">
                                        {employee.firstName} {employee.lastName}
                                      </div>
                                      <div className="text-xs text-gray-500 truncate">
                                        ID: {employee.employeeId}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              No employees found
                            </div>
                          )}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('status', { defaultValue: 'Status' })}
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('allStatuses', { defaultValue: 'All Statuses' })}</option>
                    <option value="present">{t('present')}</option>
                    <option value="absent">{t('absent')}</option>
                    <option value="half_day">{t('halfDay')}</option>
                    <option value="late">{t('late')}</option>
                    <option value="early_leave">{t('earlyLeave')}</option>
                    <option value="overtime">{t('overtime')}</option>
                    <option value="holiday">{t('holiday')}</option>
                    <option value="leave">{t('leave')}</option>
                    <option value="weekend">{t('weekend')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('startDate', { defaultValue: 'Start Date' })}
                  </label>
                  <input
                    type="date"
                    value={startDateFilter}
                    onChange={(e) => setStartDateFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('endDate', { defaultValue: 'End Date' })}
                  </label>
                  <input
                    type="date"
                    value={endDateFilter}
                    onChange={(e) => setEndDateFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setEmployeeFilter('');
                    setStatusFilter('');
                    setStartDateFilter('');
                    setEndDateFilter('');
                    setEmployeeFilterSearch('');
                    setShowEmployeeFilterDropdown(false);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('clearFilters', { defaultValue: 'Clear Filters' })}
                </button>
              </div>
            </div>
          )}

          {/* Search Results Indicator */}
          {searchTerm && activeTabData?.data && (
            <div className="mb-4">
              <div className="inline-flex items-center px-4 py-2 bg-blue-50/80 backdrop-blur-sm border border-blue-200/50 rounded-2xl text-sm text-blue-700 font-medium">
                <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                Showing {
                  activeTabData.data.filter((item) => {
                    const searchLower = searchTerm.toLowerCase();
                    const employeeName = item.employeeId?.firstName && item.employeeId?.lastName
                      ? `${item.employeeId.firstName} ${item.employeeId.lastName}`.toLowerCase()
                      : '';
                    const employeeId = item.employeeId?.employeeId?.toLowerCase() || '';
                    return (
                      employeeName.includes(searchLower) ||
                      employeeId.includes(searchLower) ||
                      item.status?.toLowerCase().includes(searchLower) ||
                      item.date?.includes(searchLower)
                    );
                  }).length
                } of {activeTabData.data.length} {t('attendanceRecords', { defaultValue: 'attendance records' })}
              </div>
            </div>
          )}

          {/* Table */}
          {activeTabData?.loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-2 text-secondary-600">{t('loading')}...</span>
            </div>
          ) : activeTabData?.error ? (
            <div className="text-center py-12">
              <div className="text-error-600 mb-2">{t('errorLoadingAttendance', { defaultValue: 'Error loading attendance data' })}</div>
              <button
                onClick={() => refetchAttendance()}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                {t('tryAgain')}
              </button>
            </div>
          ) : (
            <>
              {renderTable()}
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    {t('showing', { defaultValue: 'Showing' })} {((currentPage - 1) * itemsPerPage) + 1} {t('to', { defaultValue: 'to' })} {Math.min(currentPage * itemsPerPage, totalItems)} {t('of', { defaultValue: 'of' })} {totalItems} {t('entries', { defaultValue: 'entries' })}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="p-2 bg-white/80 backdrop-blur-xl border border-white/20 rounded-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
                    </button>
                    <span className="px-4 py-2 bg-blue-500/20 backdrop-blur-sm border border-white/20 rounded-lg text-sm font-medium text-blue-700">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 bg-white/80 backdrop-blur-xl border border-white/20 rounded-lg shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
                    >
                      <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && selectedItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-secondary-900 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-xl sm:rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl xl:max-w-5xl">
              <div className="bg-white px-3 pt-4 pb-3 sm:px-6 sm:pt-5 sm:pb-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                    Attendance Details
                  </h3>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-secondary-400 hover:text-secondary-600 transition-colors duration-200 p-1"
                  >
                    <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="space-y-4 sm:space-y-6">
                  {/* Selfie Section */}
                  {(selectedItem.clockInSelfie || selectedItem.clockOutSelfie) && (
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4">
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Selfie Verification</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        {selectedItem.clockInSelfie && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">Clock In Selfie</p>
                            <div className="relative group">
                              <img
                                src={selectedItem.clockInSelfie}
                                alt="Clock In Selfie"
                                className="w-full h-32 sm:h-40 lg:h-48 object-cover rounded-lg shadow-md cursor-pointer transition-transform duration-200 group-hover:scale-105 border-2 border-green-200"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedImage({
                                    src: selectedItem.clockInSelfie,
                                    alt: 'Clock In Selfie',
                                    type: 'Clock In'
                                  });
                                  setShowImageModal(true);
                                }}
                              />
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center pointer-events-none"
                              >
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-lg px-3 py-1">
                                  <span className="text-gray-800 text-sm font-medium">Click to enlarge</span>
                                </div>
                              </div>
                            </div>
                            {selectedItem.actualInTime && (
                              <p className="text-xs text-gray-500">
                                Taken at: {new Date(selectedItem.actualInTime).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                        {selectedItem.clockOutSelfie && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">Clock Out Selfie</p>
                            <div className="relative group">
                              <img
                                src={selectedItem.clockOutSelfie}
                                alt="Clock Out Selfie"
                                className="w-full h-32 sm:h-40 lg:h-48 object-cover rounded-lg shadow-md cursor-pointer transition-transform duration-200 group-hover:scale-105 border-2 border-red-200"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedImage({
                                    src: selectedItem.clockOutSelfie,
                                    alt: 'Clock Out Selfie',
                                    type: 'Clock Out'
                                  });
                                  setShowImageModal(true);
                                }}
                              />
                              <div 
                                className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center pointer-events-none"
                              >
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-lg px-3 py-1">
                                  <span className="text-gray-800 text-sm font-medium">Click to enlarge</span>
                                </div>
                              </div>
                            </div>
                            {selectedItem.actualOutTime && (
                              <p className="text-xs text-gray-500">
                                Taken at: {new Date(selectedItem.actualOutTime).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Main Content Grid */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                    {/* Left Column */}
                    <div className="space-y-4 sm:space-y-6">
                      {/* Employee Information */}
                      <div>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Employee Information</h4>
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-3">
                          <div className="flex items-center space-x-3">
                            <UserCircleIcon className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 truncate">
                                {selectedItem.employeeId?.firstName} {selectedItem.employeeId?.lastName}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600">ID: {selectedItem.employeeId?.employeeId}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="font-medium">{formatDate(selectedItem.date)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Status:</span>
                              <p className={`font-medium capitalize ${
                                selectedItem.status === 'present' ? 'text-green-600' :
                                selectedItem.status === 'absent' ? 'text-red-600' :
                                selectedItem.status === 'late' ? 'text-yellow-600' :
                                selectedItem.status === 'half_day' ? 'text-blue-600' :
                                selectedItem.status === 'overtime' ? 'text-purple-600' :
                                'text-gray-600'
                              }`}>
                                {selectedItem.status.replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Time Information */}
                      <div>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Time Information</h4>
                        <div className="bg-blue-50 rounded-lg p-3 sm:p-4 space-y-3">
                          {selectedItem.scheduledInTime && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                              <span className="text-sm text-gray-600">Scheduled Time:</span>
                              <span className="font-medium text-sm">
                                {new Date(selectedItem.scheduledInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                                {new Date(selectedItem.scheduledOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                          {selectedItem.actualInTime && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                              <span className="text-sm text-gray-600">Clock In:</span>
                              <span className="font-medium text-green-600 text-sm">
                                {new Date(selectedItem.actualInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                          {selectedItem.actualOutTime && (
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                              <span className="text-sm text-gray-600">Clock Out:</span>
                              <span className="font-medium text-red-600 text-sm">
                                {new Date(selectedItem.actualOutTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Work Summary */}
                      <div>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Work Summary</h4>
                        <div className="bg-green-50 rounded-lg p-3 sm:p-4 space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                            <span className="text-sm text-gray-600">Work Minutes:</span>
                            <span className="font-medium text-sm">{selectedItem.workMinutes || 0} min</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                            <span className="text-sm text-gray-600">Break Minutes:</span>
                            <span className="font-medium text-sm">{selectedItem.breakMinutes || 0} min</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                            <span className="text-sm text-gray-600">Overtime Minutes:</span>
                            <span className="font-medium text-purple-600 text-sm">{selectedItem.overtimeMinutes || 0} min</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                            <span className="text-sm text-gray-600">Late Minutes:</span>
                            <span className="font-medium text-yellow-600 text-sm">{selectedItem.lateMinutes || 0} min</span>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-0">
                            <span className="text-sm text-gray-600">Early Leave Minutes:</span>
                            <span className="font-medium text-orange-600 text-sm">{selectedItem.earlyLeaveMinutes || 0} min</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4 sm:space-y-6">
                    {/* Device Information */}
                    {(selectedItem.clockInDevice || selectedItem.clockOutDevice) && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Device Information</h4>
                        <div className="bg-purple-50 rounded-lg p-4 space-y-3">
                          {selectedItem.clockInDevice && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Clock In Device:</p>
                              <div className="flex items-center space-x-2 text-sm">
                                {selectedItem.clockInDevice.type === 'mobile' ? (
                                  <DevicePhoneMobileIcon className="h-4 w-4 text-blue-500" />
                                ) : (
                                  <ComputerDesktopIcon className="h-4 w-4 text-blue-500" />
                                )}
                                <span>{selectedItem.clockInDevice.type}</span>
                                {selectedItem.clockInDevice.deviceId && (
                                  <span className="text-gray-500">• {selectedItem.clockInDevice.deviceId}</span>
                                )}
                              </div>
                            </div>
                          )}
                          {selectedItem.clockOutDevice && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Clock Out Device:</p>
                              <div className="flex items-center space-x-2 text-sm">
                                {selectedItem.clockOutDevice.type === 'mobile' ? (
                                  <DevicePhoneMobileIcon className="h-4 w-4 text-red-500" />
                                ) : (
                                  <ComputerDesktopIcon className="h-4 w-4 text-red-500" />
                                )}
                                <span>{selectedItem.clockOutDevice.type}</span>
                                {selectedItem.clockOutDevice.deviceId && (
                                  <span className="text-gray-500">• {selectedItem.clockOutDevice.deviceId}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Location Information */}
                    {(selectedItem.clockInLocation || selectedItem.clockOutLocation) && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Location Information</h4>
                        <div className="bg-indigo-50 rounded-lg p-4 space-y-3">
                          {selectedItem.clockInLocation && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Clock In Location:</p>
                              <div className="flex items-start space-x-2 text-sm">
                                <MapIcon className="h-4 w-4 text-green-500 mt-0.5" />
                                <div>
                                  <p>
                                    {selectedItem.employeeId?.workLocation === 'Remote' 
                                      ? (selectedItem.clockInLocation.formattedAddress || selectedItem.clockInLocation.address || 'Address not available')
                                      : (selectedItem.clockInLocation.address || 'Address not available')
                                    }
                                  </p>
                                  <p className="text-gray-500">
                                    Lat: {selectedItem.clockInLocation.latitude?.toFixed(6)}, 
                                    Lng: {selectedItem.clockInLocation.longitude?.toFixed(6)}
                                  </p>
                                  {selectedItem.clockInLocation.accuracy && (
                                    <p className="text-gray-500">Accuracy: ±{selectedItem.clockInLocation.accuracy}m</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          {selectedItem.clockOutLocation && (
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-2">Clock Out Location:</p>
                              <div className="flex items-start space-x-2 text-sm">
                                <MapIcon className="h-4 w-4 text-red-500 mt-0.5" />
                                <div>
                                  <p>
                                    {selectedItem.employeeId?.workLocation === 'Remote' 
                                      ? (selectedItem.clockOutLocation.formattedAddress || selectedItem.clockOutLocation.address || 'Address not available')
                                      : (selectedItem.clockOutLocation.address || 'Address not available')
                                    }
                                  </p>
                                  <p className="text-gray-500">
                                    Lat: {selectedItem.clockOutLocation.latitude?.toFixed(6)}, 
                                    Lng: {selectedItem.clockOutLocation.longitude?.toFixed(6)}
                                  </p>
                                  {selectedItem.clockOutLocation.accuracy && (
                                    <p className="text-gray-500">Accuracy: ±{selectedItem.clockOutLocation.accuracy}m</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Remarks */}
                    {selectedItem.remarks && (
                      <div>
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Remarks</h4>
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                          <p className="text-sm text-gray-700">{selectedItem.remarks}</p>
                        </div>
                      </div>
                    )}

                    {/* Exceptions */}
                    {selectedItem.exceptions && Array.isArray(selectedItem.exceptions) && selectedItem.exceptions.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Exceptions</h4>
                        <div className="space-y-2">
                          {selectedItem.exceptions.map((exception, index) => (
                            <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-yellow-800 capitalize">
                                  {exception.type.replace('_', ' ')}
                                </span>
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  exception.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                  exception.status === 'approved' ? 'bg-green-200 text-green-800' :
                                  'bg-red-200 text-red-800'
                                }`}>
                                  {exception.status}
                                </span>
                              </div>
                              {exception.description && (
                                <p className="text-sm text-yellow-700 mt-1">{exception.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    Close
                  </button>
                  {isUserAdmin && (
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        handleEdit(selectedItem);
                      }}
                      className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                    >
                      Edit Record
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && isUserAdmin && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" aria-hidden="true">
            </div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-display font-semibold text-gray-900">
                    {showAddModal ? 'Add Attendance Record' : 'Edit Attendance Record'}
                  </h3>
                  <button
                    onClick={closeModals}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {renderForm()}

                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={closeModals}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                    >
                      {showAddModal ? 'Add Record' : 'Update Record'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && isUserAdmin && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-secondary-900 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-error-100 rounded-full flex items-center justify-center">
                    <TrashIcon className="h-6 w-6 text-error-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-display font-semibold text-secondary-900">
                      {t('delete')} {t('attendance', { defaultValue: 'Attendance' })}
                    </h3>
                    <p className="text-sm text-secondary-500">
                      {t('deleteAttendanceConfirmation', { defaultValue: 'Are you sure you want to delete this attendance record?' })}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeModals}
                    className="px-4 py-2 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-error-600 hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500 transition-all duration-200"
                  >
                    {t('delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center p-4" 
          style={{ zIndex: 10000 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowImageModal(false);
            }
          }}
        >
          <div className="relative max-w-4xl max-h-full">
            {/* Close button */}
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-2 right-2 z-10 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Image container */}
            <div className="bg-white rounded-lg overflow-hidden shadow-2xl max-w-full max-h-full">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="text-lg font-semibold text-gray-900">{selectedImage.type} Selfie</h3>
                <p className="text-sm text-gray-600 mt-1">Click outside, press ESC, or click the X to close</p>
              </div>
              <div className="p-4 flex justify-center">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png'; // Fallback image
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Attendance;
